import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  GET_DISCORD_VIP_ROLE_SETTINGS,
  SET_DISCORD_VIP_ROLE,
  SET_VIP_ROLE_SYNC_ENABLED,
} from "../../../../queries/mutation/broadcaster/vipQueries";

interface DiscordRole {
  id: string;
  name: string;
  color: string | null;
  assignable: boolean;
}

// Its own query, so a Discord outage only affects this card rather than the
// whole settings page.
const VipRoleSetting = () => {
  const { data, loading, error, refetch } = useQuery(
    GET_DISCORD_VIP_ROLE_SETTINGS,
    { fetchPolicy: "network-only" },
  );
  const [setVipRole, { loading: saving }] = useMutation(SET_DISCORD_VIP_ROLE, {
    // The mutation returns the fresh settings - write them straight into the
    // query so the saved state updates without a second round trip.
    update: (cache, { data: result }) => {
      cache.writeQuery({
        query: GET_DISCORD_VIP_ROLE_SETTINGS,
        data: { getDiscordVipRoleSettings: result?.setDiscordVipRole },
      });
    },
  });

  const [setSyncEnabled, { loading: togglingSync }] = useMutation(
    SET_VIP_ROLE_SYNC_ENABLED,
    {
      update: (cache, { data: result }) => {
        cache.writeQuery({
          query: GET_DISCORD_VIP_ROLE_SETTINGS,
          data: { getDiscordVipRoleSettings: result?.setVipRoleSyncEnabled },
        });
      },
    },
  );

  const handleToggleSync = async (enabled: boolean) => {
    try {
      await setSyncEnabled({ variables: { enabled } });
      toast.success(
        enabled
          ? "Automatic VIP roles on - bringing everyone's role up to date"
          : "Automatic VIP roles paused",
      );
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const settings = data?.getDiscordVipRoleSettings;
  const roles: DiscordRole[] = settings?.roles ?? [];
  const savedRoleId: string = settings?.selectedRoleId ?? "";
  const [selectedRoleId, setSelectedRoleId] = useState("");

  useEffect(() => {
    setSelectedRoleId(savedRoleId);
  }, [savedRoleId]);

  const savedRole = roles.find((r) => r.id === savedRoleId);
  const hasBlockedRoles = roles.some((r) => !r.assignable);

  const handleSave = async () => {
    try {
      await setVipRole({ variables: { roleId: selectedRoleId || null } });
      toast.success(
        selectedRoleId
          ? "VIP role saved - existing VIPs are being given it now"
          : "VIP role cleared - it's being removed from existing VIPs",
      );
    } catch (e: any) {
      toast.error(e.message);
      setSelectedRoleId(savedRoleId);
    }
  };

  return (
    <div className="p-4 bg-base-200 rounded-lg">
      <h3 className="font-semibold text-base-content">Fan Club VIP Role</h3>
      <p className="text-sm text-base-content/60 mt-1">
        The Discord role fan club members get while they're VIP. It's added
        and removed automatically for anyone with both Chaturbate and Discord
        linked.
      </p>

      {loading ? (
        <span className="loading loading-dots loading-sm mt-3" />
      ) : error ? (
        <div className="flex items-center gap-2 mt-3">
          <p className="text-xs text-error">
            Couldn't load your Discord roles: {error.message}
          </p>
          <button className="btn btn-xs" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      ) : !settings?.botInServer ? (
        <p className="text-xs text-warning mt-3">
          The bot isn't in your Discord server, so there's no role to pick yet.
        </p>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <select
              className="select select-bordered flex-1"
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              disabled={saving}
            >
              <option value="">None - don't give a Discord role</option>
              {roles.map((role) => (
                <option
                  key={role.id}
                  value={role.id}
                  disabled={!role.assignable}
                >
                  {role.name}
                  {role.assignable ? "" : " (bot can't assign)"}
                </option>
              ))}
            </select>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || selectedRoleId === savedRoleId}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>

          {!settings.canManageRoles ? (
            <p className="text-xs text-warning mt-2">
              The bot needs the Manage Roles permission in your server before
              it can give out a role.
            </p>
          ) : (
            hasBlockedRoles && (
              <p className="text-xs text-base-content/50 mt-2">
                Roles marked "bot can't assign" sit above the bot's own role.
                Drag the bot's role higher in Server Settings → Roles to
                unlock them.
              </p>
            )
          )}

          {savedRole ? (
            <p className="text-xs text-success mt-2">
              ✓ Fan club VIPs get @{savedRole.name}
            </p>
          ) : savedRoleId ? (
            <p className="text-xs text-warning mt-2">
              The saved role no longer exists in your server - pick a new one
            </p>
          ) : (
            <p className="text-xs text-base-content/50 mt-2">
              Not set - VIP status isn't shown in Discord
            </p>
          )}

          {savedRoleId && (
            <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-base-300">
              <div>
                <div className="text-sm font-semibold">
                  Automatically add &amp; remove the role
                </div>
                <p className="text-xs text-base-content/60 mt-1">
                  {settings.syncEnabled
                    ? "On - the role follows VIP status as it changes."
                    : "Paused - VIP is still tracked, but nobody's role changes. Turning it back on brings everyone's role up to date."}
                </p>
              </div>
              <input
                type="checkbox"
                className="toggle toggle-primary"
                checked={!!settings.syncEnabled}
                onChange={(e) => handleToggleSync(e.target.checked)}
                disabled={togglingSync}
                aria-label="Automatically add and remove the VIP role"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VipRoleSetting;
